import React, { ComponentType, ReactNode } from 'react';
import {
    SvgIconProps,
    ListItemButton,
    ListItem,
    ListItemIcon,
    ListItemProps,
} from '@mui/material';

import { Typography, TypographyProps } from '../../Typography';
import { Stack } from '../../Stack';
import { Checkbox } from '../../Checkbox';


export type DropdownOption<T = ReactNode> = {
    label: T;
    value: any;
    description?: ReactNode;
    icon?: ComponentType<SvgIconProps>;
};

export type ItemProps = Pick<ListItemProps, 'sx'> & {
    data: DropdownOption;
    onSelect: (data: DropdownOption) => void;
    isSelected?: boolean;
    isMulti?: boolean;
    itemTypographyProps?: TypographyProps;
};

export const Item = React.memo<ItemProps>(
    ({
        data,
        onSelect,
        isSelected,
        isMulti,
        sx = {},
        itemTypographyProps = {},
    }) => (
        <ListItem disablePadding data-testid={`dropdown-item-${data.value}`}>
            <ListItemButton
                onClick={() => onSelect(data)}
                sx={{
                    height: 36,
                    p: 2,
                    borderRadius: 2,
                    '&:hover': {
                        backgroundColor: isSelected ? 'grayscale.shade20' : 'grayscale.shade10',
                    },
                    ...isSelected && {
                        backgroundColor: 'grayscale.shade20',
                    },
                    ...sx,
                }}
            >
                {data.icon && (
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 4 }}>
                        <data.icon
                            sx={({ spacing }) => ({
                                width: spacing(6),
                                height: spacing(6),
                            })}
                        />
                    </ListItemIcon>
                )}
                <Stack flex='1'>
                    <Typography variant='mediumBody2' color='grayscale.shade95' {...itemTypographyProps}>
                        {data.label}
                    </Typography>
                    {data.description && (
                        <Typography
                            variant='regularCaption1'
                            color='grayscale.shade85'
                        >
                            {data.description}
                        </Typography>
                    )}
                </Stack>
                {isMulti && (
                    <ListItemIcon sx={{ ml: 2, minWidth: 'auto' }}>
                        <Checkbox
                            color='primary'
                            checked={isSelected}
                            onChange={() => onSelect(data)}
                        />
                    </ListItemIcon>
                )}
            </ListItemButton>
        </ListItem>
    ),
);
